import { useEffect, useState } from 'react';
import { config } from '../config';
import { apiService } from '../services/apiService';
import { auth } from '../config/firebase';

/**
 * Capability flags for this deployment/tenant.
 *
 * Source of truth is the backend GET /api/settings/features, which resolves the
 * per-tenant `tenant-config.tenants.features` (managed in the super-admin portal)
 * with the deployment env as fallback. We fetch it ONCE (module-cached, shared by
 * every consumer) and seed the initial value from the build-time config so the UI
 * renders immediately and degrades gracefully if the request fails.
 *
 * The endpoint requires a Firebase token, so the fetch is gated on auth being
 * ready — it fires when `onAuthStateChanged` reports a signed-in user, not blindly
 * on mount (mounting happens before Firebase restores the session → 401).
 */
export interface FeatureFlags {
  liveIntelligence: boolean;
  training: boolean;
}

const buildTimeFlags: FeatureFlags = {
  liveIntelligence: config.features?.liveIntelligence ?? false,
  training: config.features?.training ?? false,
};

// Module-level cache shared across all hook consumers + a tiny subscription so a
// late backend response re-renders every mounted useFeatures() caller.
let cachedFlags: FeatureFlags = buildTimeFlags;
let fetchStarted = false;
const listeners = new Set<() => void>();

async function loadFeaturesOnce(): Promise<void> {
  if (fetchStarted) return;
  // Wait for auth — without a signed-in user there's no token to send, so the
  // request would 401. onAuthStateChanged re-invokes this once the user restores.
  if (!auth.currentUser) return;
  fetchStarted = true;
  try {
    const res = await apiService.get<{ features: Partial<FeatureFlags> }>('/settings/features');
    const f = res?.data?.features;
    if (f && typeof f.liveIntelligence === 'boolean') {
      cachedFlags = {
        liveIntelligence: !!f.liveIntelligence,
        training: !!f.training,
      };
      listeners.forEach((notify) => notify());
    }
  } catch {
    // Network/auth failure → keep the build-time fallback already in cachedFlags.
    fetchStarted = false; // allow a later retry (e.g. after the next auth change)
  }
}

export function useFeatures(): FeatureFlags {
  const [flags, setFlags] = useState<FeatureFlags>(cachedFlags);

  useEffect(() => {
    const notify = () => setFlags(cachedFlags);
    listeners.add(notify);
    // Fetch now if already authed, and (re)try whenever auth becomes ready.
    loadFeaturesOnce();
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) loadFeaturesOnce();
    });
    // If the cache was already updated before this effect ran, sync now.
    if (flags !== cachedFlags) setFlags(cachedFlags);
    return () => {
      listeners.delete(notify);
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return flags;
}
